import { useState, type FormEvent } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Modal } from '../../components/ui/Modal'
import { Field } from '../../components/ui/Field'
import { Input, Select, Textarea } from '../../components/ui/Input'
import { Button } from '../../components/ui/Button'
import { ConfirmDialog } from '../../components/ui/ConfirmDialog'
import { PermissionGate } from '../../components/PermissionGate'
import { brainstormingApi } from '../../api/brainstorming.api'
import { tasksApi } from '../../api/tasks.api'
import { toast } from '../../store/toastStore'
import { ApiError } from '../../api/client'
import { BRAINSTORM_CATEGORY_LABELS } from './brainstormLabels'
import { ProgressSlider } from '../tasks/shared/ProgressSlider'
import { ProgressReadonlyPill } from '../tasks/shared/ProgressReadonlyPill'
import formStyles from '../../components/ui/Form.module.css'
import styles from './BrainstormEntryFormModal.module.css'
import type { BrainstormCategory, BrainstormEntry } from '../../api/types'

const CATEGORY_OPTIONS = Object.keys(BRAINSTORM_CATEGORY_LABELS) as BrainstormCategory[]

export function BrainstormEntryFormModal({
  projectId,
  entry,
  onClose,
}: {
  projectId: string
  entry?: BrainstormEntry | null
  onClose: () => void
}) {
  const isEdit = Boolean(entry)
  const queryClient = useQueryClient()

  const [category, setCategory] = useState<BrainstormCategory>(entry?.category ?? 'CONCEPT')
  const [title, setTitle] = useState(entry?.title ?? '')
  const [body, setBody] = useState(entry?.body ?? '')
  const [progress, setProgress] = useState(entry?.progress ?? 0)
  const [taskIds, setTaskIds] = useState<string[]>(entry?.tasks.map((t) => t.taskId) ?? [])
  const [error, setError] = useState<string | null>(null)
  const [confirmDelete, setConfirmDelete] = useState(false)

  const hasLinkedTasks = taskIds.length > 0

  const tasksQuery = useQuery({
    queryKey: ['tasks', { projectId, forLinking: true }],
    queryFn: async () => (await tasksApi.list({ projectId, pageSize: 500 })).data,
  })

  const linkedTaskProgress = hasLinkedTasks
    ? Math.round(taskIds.reduce((sum, id) => sum + (tasksQuery.data?.find((t) => t.id === id)?.progress ?? 0), 0) / taskIds.length)
    : progress

  const saveMutation = useMutation({
    mutationFn: async () => {
      const id = isEdit && entry ? entry.id : (await brainstormingApi.create({ projectId, category, title, body })).data.id
      if (isEdit && entry) await brainstormingApi.update(id, { category, title, body, progress: hasLinkedTasks ? undefined : progress })
      await brainstormingApi.setTasks(id, taskIds)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['brainstorming', projectId] })
      toast.success(isEdit ? 'Eintrag aktualisiert.' : 'Eintrag angelegt.')
      onClose()
    },
    onError: (err) => setError(err instanceof ApiError ? err.message : 'Speichern fehlgeschlagen.'),
  })

  const deleteMutation = useMutation({
    mutationFn: () => brainstormingApi.remove(entry!.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['brainstorming', projectId] })
      toast.success('Eintrag gelöscht.')
      onClose()
    },
  })

  const onSubmit = (e: FormEvent) => {
    e.preventDefault()
    setError(null)
    saveMutation.mutate()
  }

  return (
    <Modal
      title={isEdit ? 'Eintrag bearbeiten' : 'Neuer Brainstorming-Eintrag'}
      onClose={onClose}
      size="lg"
      footer={
        <>
          {isEdit ? (
            <PermissionGate anyOf={['brainstorming.manage']}>
              <Button variant="danger" onClick={() => setConfirmDelete(true)} style={{ marginRight: 'auto' }}>
                Löschen
              </Button>
            </PermissionGate>
          ) : null}
          <Button variant="ghost" onClick={onClose}>
            Abbrechen
          </Button>
          <Button variant="primary" onClick={onSubmit} loading={saveMutation.isPending}>
            Speichern
          </Button>
        </>
      }
    >
      <form onSubmit={onSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
        {error ? <div style={{ color: 'var(--danger)', fontSize: 'var(--text-sm)' }}>{error}</div> : null}

        <Field label="Kategorie" htmlFor="bs-category">
          <Select id="bs-category" value={category} onChange={(e) => setCategory(e.target.value as BrainstormCategory)}>
            {CATEGORY_OPTIONS.map((c) => (
              <option key={c} value={c}>
                {BRAINSTORM_CATEGORY_LABELS[c]}
              </option>
            ))}
          </Select>
        </Field>

        <Field label="Titel" htmlFor="bs-title">
          <Input id="bs-title" value={title} onChange={(e) => setTitle(e.target.value)} required autoFocus />
        </Field>

        <Field label="Inhalt" htmlFor="bs-body" hint="Markdown wird unterstützt, z.B. ```lua … ``` für Script-Grundgerüste.">
          <Textarea id="bs-body" value={body} onChange={(e) => setBody(e.target.value)} style={{ minHeight: 240, fontFamily: 'var(--font-mono)' }} />
        </Field>

        <Field label="Fortschritt" htmlFor="bs-progress" hint={hasLinkedTasks ? 'Ergibt sich aus den verknüpften Aufgaben — dort erledigen statt hier eintragen.' : undefined}>
          {hasLinkedTasks ? (
            <ProgressReadonlyPill value={linkedTaskProgress} />
          ) : (
            <ProgressSlider id="bs-progress" value={progress} onCommit={setProgress} />
          )}
        </Field>

        <Field label="Verknüpfte Aufgaben">
          <div className={styles.taskList}>
            {tasksQuery.data?.map((task) => (
              <label key={task.id} className={`${formStyles.checkboxRow} ${styles.taskRow}`}>
                <input
                  type="checkbox"
                  checked={taskIds.includes(task.id)}
                  onChange={() => setTaskIds((p) => (p.includes(task.id) ? p.filter((x) => x !== task.id) : [...p, task.id]))}
                />
                {task.title}
              </label>
            ))}
            {tasksQuery.data?.length === 0 ? <span style={{ color: 'var(--text-muted)', fontSize: 'var(--text-sm)' }}>Keine Aufgaben im Projekt.</span> : null}
          </div>
        </Field>
      </form>

      {confirmDelete ? (
        <ConfirmDialog
          title="Eintrag löschen"
          message={`"${entry?.title}" wirklich löschen?`}
          danger
          loading={deleteMutation.isPending}
          onConfirm={() => deleteMutation.mutate()}
          onCancel={() => setConfirmDelete(false)}
        />
      ) : null}
    </Modal>
  )
}
